import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { Box, Card, Grid, LinearProgress, Typography, CircularProgress, Alert, Stack, Tabs, Tab } from '@mui/material';
import { swedishVocabulary } from '../data/swedish';
import { SetProgressCard } from './SetProgressCard';

const GET_PROGRESS = gql`
  query GetLanguageProgress($language: String!) {
    getLanguageProgress(language: $language) {
      id
      language
      setId
      wordId
      mode
      mastery
    }
  }
`;

interface SetSelectorProps {
  onSelectSet: (setId: number) => void;
}

export const SetSelector: React.FC<SetSelectorProps> = ({ onSelectSet }) => {
  const [selectedMode, setSelectedMode] = React.useState<'normal' | 'reverse' | 'fill'>('normal');

  const { loading, error, data } = useQuery(GET_PROGRESS, {
    variables: { language: 'swedish' },
  });

  // Parse progress array into a fast-lookup dictionary: key = `${setId}_${wordId}` -> mastery
  const progressMap = React.useMemo(() => {
    const map: { [key: string]: number } = {};
    if (data?.getLanguageProgress) {
      data.getLanguageProgress.forEach((p: any) => {
        const pMode = p.mode || 'normal';
        if (pMode === selectedMode) {
          map[`${p.setId}_${p.wordId}`] = p.mastery;
        }
      });
    }
    return map;
  }, [data, selectedMode]);

  // Calculate overall progress stats
  const globalStats = React.useMemo(() => {
    const totalWords = swedishVocabulary.length;
    let masteredCount = 0;
    
    swedishVocabulary.forEach((word) => {
      const key = `${word.setId}_${word.wordId}`;
      const mastery = progressMap[key] || 0;
      if (mastery >= 5) {
        masteredCount += 1;
      }
    });

    const percent = totalWords > 0 ? Math.round((masteredCount / totalWords) * 100) : 0;
    return { masteredCount, totalWords, percent };
  }, [progressMap]);

  // Group vocabulary by sets to compute stats
  const setSummaryMap = React.useMemo(() => {
    const summary: { [setId: number]: { id: number; name: string; total: number; mastered: number } } = {};
    
    swedishVocabulary.forEach((word) => {
      if (!summary[word.setId]) {
        summary[word.setId] = {
          id: word.setId,
          name: word.setName,
          total: 0,
          mastered: 0,
        };
      }
      summary[word.setId].total += 1;
      
      const key = `${word.setId}_${word.wordId}`;
      const mastery = progressMap[key] || 0;
      if (mastery >= 5) {
        summary[word.setId].mastered += 1;
      }
    });

    return summary;
  }, [progressMap]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading progress: {error.message}
      </Alert>
    );
  }

  const sets = Object.values(setSummaryMap).sort((a, b) => a.id - b.id);

  return (
    <Box>
      {/* Overall Progress Card */}
      <Card sx={{ p: 4, mb: 4, borderRadius: 4, boxShadow: 3, backgroundColor: 'background.paper' }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            🇸🇪 Overall Swedish Mastery
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1" color="text.secondary">
              Total Vocabulary Learned
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'black', color: 'primary.main' }}>
              {globalStats.masteredCount} / {globalStats.totalWords} words ({globalStats.percent}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={globalStats.percent}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'divider',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Stack>
      </Card>

      {/* Mode Selection Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={selectedMode}
          onChange={(_e, newValue) => setSelectedMode(newValue)}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          aria-label="Vocabulary Mode Tabs"
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
            '& .MuiTab-root': {
              fontWeight: 'bold',
              py: 2,
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab value="normal" label="🇸🇪 Swedish ➔ English" />
          <Tab value="reverse" label="🇬🇧 English ➔ Swedish" />
          <Tab value="fill" label="✏️ Fill in the Blank" />
        </Tabs>
      </Box>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }}>
        Select a Vocabulary Set (35 Sets Total)
      </Typography>
      <Grid container spacing={3}>
        {sets.map((set) => (
          <Grid item xs={12} sm={6} md={4} key={set.id}>
            <SetProgressCard
              id={set.id}
              name={set.name}
              total={set.total}
              mastered={set.mastered}
              onClick={() => onSelectSet(set.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
